import { createElement } from "react";
import { Navigate } from "react-router-dom";
import { getTokenExpiration, getRoleId } from "Common/Components/getRoleId";
import { canViewSection, getLandingRoute, SectionKey } from "Common/constants/roles";
import CityRegiste from "../pages/CityRegiste/index";
import Measurement from "../pages/Measurement/index";
import ProvincialRegistration from "pages/ProvincialRegistration/Index";
import Warehouse from "pages/Warehouse";
import GoodsType from "pages/GoodsType";
import GoodsAndServices from "pages/GoodsAndServices";
import Customer from "pages/Customer";
import MinimumPayment from "pages/MinimumPayment";
import ProformaDescriptions from "pages/ProformaDescriptions";
import SalesAvailability from "pages/SalesAvailability";
import ProductRegistration from "pages/ProductRegistration";
import ProductPrice from "pages/ProductPrice";
import ProformaInvoices from "pages/ProformaInvoices";
import AllOrders from "pages/AllOrders";
import OtherOrders from "pages/OtherOrders";
import PendingPayments from "pages/PendingPayments";
import ConfirmedPayments from "pages/ConfirmedPayments";
import Contacts from "pages/Contacts";
import SalesSubstitutions from "pages/SalesSubstitutions";
import SalesOrders from "pages/SalesOrders";
import PaymentType from "pages/PaymentType";
import Currency from "pages/Currency";
import manageUser from "pages/ManageUser";
import FoamRollWarehouse from "pages/FoamRollWarehouse";
import ReadyForProduction from "pages/ReadyForProduction";
import GoodsInProduction from "pages/GoodsInProduction";
import ProducedGoods from "pages/ProducedGoods";
import ProductsWarehouse from "pages/ProductsWarehouse";
import GoodsExitedWarehouse from "pages/GoodsExitedWarehouse";
import LoadedGoods from "pages/LoadedGoods";
import DeliveredGoods from "pages/DeliveredGoods";
import OrdersInProduction from "pages/OrdersInProduction";
import ProducedOrders from "pages/ProducedOrders";
import OrdersInWarehouse from "pages/OrdersInWarehouse";
import OrdersExitedWarehouse from "pages/OrdersExitedWarehouse";
import LoadedOrders from "pages/LoadedOrders";
import DeliveredOrders from "pages/DeliveredOrders";
import NavTabs from "pages/Components/Navigation/Tabs";
import Login from "pages/Authentication/Login";
import Logout from "pages/Authentication/LogOut";
import UserProfile from "pages/Authentication/UserProfile";
import Settings from "pages/Pages/Settings";

// دسترسی مسیرها از همان نگاشت نقش↔بخش منو می‌آید (Common/constants/roles.ts) تا منو و
// روت هرگز از هم جدا نیفتند. مسیرهایی که بخش مشخصی ندارند برای همه‌ی کاربران واردشده بازند.

// کلیدهای واقعی localStorage که همه‌جای اپ (Login.tsx، ApiInterceptor/Api.tsx، ...) استفاده می‌شوند.
const clearAuth = () => {
  localStorage.removeItem("app_at_mahan");
  localStorage.removeItem("app_rt");
};

// اگر توکن نباشد یا منقضی شده باشد، نشست را پاک می‌کند تا کاربر به لاگین برود.
const isAuthValid = () => {
  const accessToken = localStorage.getItem("app_at_mahan");
  if (!accessToken) {
    clearAuth();
    return false;
  }

  // اگر نتوانیم انقضا را از توکن دربیاوریم (فرمت غیرمنتظره یا بدون claim exp)، کاربر را به‌زور
  // خارج نمی‌کنیم؛ فقط وقتی صریحاً انقضا مشخص و گذشته باشد نشست را باطل می‌کنیم. تشخیص واقعی
  // انقضا نهایتاً با پاسخ 401 از خود API انجام می‌شود (نگاه کنید به Api.tsx).
  const tokenExpiration = getTokenExpiration(accessToken);
  if (tokenExpiration && tokenExpiration.getTime() <= Date.now()) {
    clearAuth();
    return false;
  }

  return true;
};

// این فایل عمداً .ts مانده (نه .tsx)؛ به‌جای JSX از createElement استفاده شده.
const RedirectToLogin = () =>
  createElement(Navigate, { to: "/login", replace: true });

// اگر نشست معتبر است اما آدرس با هیچ route مجازی تطبیق ندارد (مثلاً ورود از /)،
// کاربر را به صفحه‌ی مجاز نقش خودش برگردان؛ صفحه‌ی NotFound/«در حال توسعه» مقصد نشست‌دار نیست.
const RedirectToLanding = () => {
  const roleId = getRoleId(localStorage.getItem("app_at_mahan"));
  return createElement(Navigate, { to: getLandingRoute(roleId), replace: true });
};

const publicRoutes = [
  { path: "/login", component: Login },
  { path: "/logout", component: Logout },
  { path: "*", component: RedirectToLogin },
];

const Routepage = () => {
  if (!isAuthValid()) {
    // فقط روت‌های عمومی برگردانده می‌شود؛ هر مسیری غیر از لاگین/خروج به لاگین هدایت می‌شود.
    return [[], publicRoutes] as const;
  }

  const accessToken = localStorage.getItem("app_at_mahan");
  // role_id لزوماً در payload توکن این API نیست؛ اگر قابل‌تشخیص نبود، به‌جای قفل‌کردن کاربر همه‌ی
  // مسیرها را نشان می‌دهیم (کنترل واقعی دسترسی همیشه با خود API است، این فقط برای UI است).
  const userRole = getRoleId(accessToken);

  // section مشخص می‌کند این مسیر به کدام بخش منو تعلق دارد. مسیرهای بدون section
  // (پروفایل و صفحه‌ی نیافته) برای همه‌ی کاربران واردشده باز هستند.
  const authProtectedRoutes: {
    path: string;
    component: any;
    section?: SectionKey;
  }[] = [
    { path: "/ProvincialRegistration", component: ProvincialRegistration, section: "initialData" },
    { path: "/CityRegiste", component: CityRegiste, section: "initialData" },
    { path: "/Measurementpage", component: Measurement, section: "initialData" },
    { path: "/WarehouseRegistration", component: Warehouse, section: "initialData" },
    { path: "/GoodsTypeRegistration", component: GoodsType, section: "initialData" },
    { path: "/GoodsAndServicesRegistration", component: GoodsAndServices, section: "initialData" },
    { path: "/CustomerRegistration", component: Customer, section: "initialData" },
    { path: "/MinimumPaymentSettings", component: MinimumPayment, section: "minimumPayment" },
    { path: "/ProductRegistration", component: ProductRegistration, section: "initialData" },
    { path: "/ProductPriceRegistration", component: ProductPrice, section: "initialData" },
    { path: "/PaymentTypeRegistration", component: PaymentType, section: "initialData" },
    { path: "/ProformaDescriptions", component: ProformaDescriptions, section: "initialData" },
    { path: "/SalesAvailability", component: SalesAvailability, section: "salesAvailability" },
    { path: "/CurrencyRegistration", component: Currency, section: "initialData" },
    { path: "/ContactRegistration", component: Contacts, section: "initialData" },
    { path: "/SalesSubstitutions", component: SalesSubstitutions, section: "salesSubstitutions" },
    { path: "/FoamRollWarehouse", component: FoamRollWarehouse, section: "initialData" },

    { path: "/manageUser", component: manageUser, section: "userManagement" },

    { path: "/ProformaInvoices", component: ProformaInvoices, section: "proformaInvoices" },
    { path: "/AllOrders", component: AllOrders, section: "allOrders" },
    { path: "/OtherOrders", component: OtherOrders, section: "otherOrders" },
    { path: "/PendingPayments", component: PendingPayments, section: "pendingPayments" },
    { path: "/ConfirmedPayments", component: ConfirmedPayments, section: "confirmedPayments" },
    { path: "/SalesOrders", component: SalesOrders, section: "salesOrders" },

    { path: "/ReadyToProduceGoods", component: ReadyForProduction, section: "readyToProduceGoods" },
    { path: "/GoodsInProduction", component: GoodsInProduction, section: "goodsInProduction" },
    { path: "/ProducedGoods", component: ProducedGoods, section: "producedGoods" },
    { path: "/ProductsWarehouse", component: ProductsWarehouse, section: "productsWarehouse" },
    { path: "/GoodsExitedWarehouse", component: GoodsExitedWarehouse, section: "goodsExitedWarehouse" },
    { path: "/LoadedGoods", component: LoadedGoods, section: "loadedGoods" },
    { path: "/DeliveredGoods", component: DeliveredGoods, section: "deliveredGoods" },

    { path: "/OrdersInProduction", component: OrdersInProduction, section: "ordersInProduction" },
    { path: "/ProducedOrders", component: ProducedOrders, section: "producedOrders" },
    { path: "/OrdersInWarehouse", component: OrdersInWarehouse, section: "ordersInWarehouse" },
    { path: "/OrdersExitedWarehouse", component: OrdersExitedWarehouse, section: "ordersExitedWarehouse" },
    { path: "/LoadedOrders", component: LoadedOrders, section: "loadedOrders" },
    { path: "/DeliveredOrders", component: DeliveredOrders, section: "deliveredOrders" },

    { path: "/navigation-tabs", component: NavTabs, section: "initialData" },
    { path: "/pages-account-settings", component: Settings, section: "initialData" },

    { path: "/user-profile", component: UserProfile },
    { path: "*", component: RedirectToLanding },
  ];

  const filteredRoutes =
    userRole === null || userRole === undefined
      ? authProtectedRoutes
      : authProtectedRoutes.filter(
          (route) => !route.section || canViewSection(userRole, route.section),
        );

  return [filteredRoutes, publicRoutes] as const;
};

export default Routepage;
